import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type AppRole = "admin" | "gestor" | "consulta";

const roleLabels: Record<AppRole, string> = {
  admin: "Administrador",
  gestor: "Gestor",
  consulta: "Consulta",
};

export const useUserRole = () => {
  const { user } = useAuth();
  const [role, setRole] = useState<AppRole | null>(null);
  const [orgaoNome, setOrgaoNome] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setRole(null);
      setOrgaoNome(null);
      setLoading(false);
      return;
    }

    const fetchRoleAndOrgao = async () => {
      try {
        const roleRes = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .limit(1)
          .single();

        setRole((roleRes.data?.role as AppRole) ?? null);
        setLoading(false);

        const profileRes = await supabase
          .from("profiles")
          .select("orgao_id")
          .eq("user_id", user.id)
          .limit(1)
          .single();

        if (profileRes.data?.orgao_id) {
          const { data: orgao } = await supabase
            .from("orgaos")
            .select("nome")
            .eq("id", profileRes.data.orgao_id)
            .single();
          setOrgaoNome(orgao?.nome ?? null);
        } else {
          setOrgaoNome(null);
        }
      } catch {
        setLoading(false);
      }
    };

    fetchRoleAndOrgao();
  }, [user]);

  const refetch = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const roleRes = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .limit(1)
        .single();
      setRole((roleRes.data?.role as AppRole) ?? null);
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  };

  const claimAdmin = async (): Promise<boolean> => {
    try {
      const { data } = await supabase.rpc("claim_admin_if_none");
      if (data) await refetch();
      return !!data;
    } catch {
      return false;
    }
  };

  return { role, roleLabel: role ? roleLabels[role] : null, orgaoNome, loading, refetch, claimAdmin };
};
